/**
 * Essenciais da mala — regras no cliente (Home v2, PO 19/09/2026).
 *
 * "Ninguém deixa escova de dentes; ninguém viaja para fora sem passaporte."
 * A Home antes da viagem confere se itens assim já estão na lista da pessoa
 * e aponta os que faltam. Só nomes: a comparação é por texto normalizado
 * (sem acento, minúsculas) contra sinônimos — "Passaportes das crianças"
 * conta como passaporte. Nada disso vai à API; é lembrete, não obrigação.
 */

export interface Essencial {
  id: string;
  rotulo: string;
  /** Fragmentos que, presentes no título de um item, satisfazem o essencial. */
  sinonimos: readonly string[];
  /** Quando a regra vale. */
  quando: 'sempre' | 'fora-do-fuso' | 'longa';
}

export const ESSENCIAIS: readonly Essencial[] = [
  { id: 'documentos', rotulo: 'Documentos', sinonimos: ['documento', 'identidade', 'rg', 'cnh', 'passaporte'], quando: 'sempre' },
  { id: 'carregador', rotulo: 'Carregador', sinonimos: ['carregador', 'cabo usb', 'powerbank', 'power bank'], quando: 'sempre' },
  { id: 'escova', rotulo: 'Escova de dentes', sinonimos: ['escova', 'higiene', 'necessaire', 'nécessaire'], quando: 'sempre' },
  { id: 'passaporte', rotulo: 'Passaporte', sinonimos: ['passaporte', 'visto'], quando: 'fora-do-fuso' },
  { id: 'adaptador', rotulo: 'Adaptador de tomada', sinonimos: ['adaptador', 'tomada'], quando: 'fora-do-fuso' },
  { id: 'seguro', rotulo: 'Seguro viagem', sinonimos: ['seguro', 'apolice', 'apólice'], quando: 'fora-do-fuso' },
  { id: 'remedios', rotulo: 'Remédios', sinonimos: ['remedio', 'remédio', 'medicamento', 'farmacia', 'farmácia'], quando: 'longa' },
];

export function normalizar(texto: string): string {
  return texto
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
}

export interface ContextoDaMala {
  /** A viagem acontece num fuso diferente do aparelho — provável "para fora". */
  foraDoFuso: boolean;
  /** Dias de viagem. */
  dias: number;
}

/**
 * Os essenciais que ainda não aparecem na lista. Ordem: a da tabela.
 */
export function essenciaisFaltando(titulos: readonly string[], ctx: ContextoDaMala): Essencial[] {
  const lista = titulos.map(normalizar);
  return ESSENCIAIS.filter((e) => {
    if (e.quando === 'fora-do-fuso' && !ctx.foraDoFuso) return false;
    if (e.quando === 'longa' && ctx.dias <= 5) return false;
    const sin = e.sinonimos.map(normalizar);
    return !lista.some((t) => sin.some((s) => t.includes(s)));
  });
}

/** O fuso da viagem é outro que o do aparelho? Falha fechado (false) se não der para saber. */
export function foraDoFusoDoAparelho(fusoDaViagem: string | null | undefined): boolean {
  try {
    const doAparelho = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return Boolean(fusoDaViagem) && Boolean(doAparelho) && fusoDaViagem !== doAparelho;
  } catch {
    return false;
  }
}
